import { useEffect, useId, useRef, useState } from 'react';

export function KnowledgeAssignmentPicker({
	label,
	options,
	selected,
	disabled,
	onChange,
}: {
	label: string;
	options: { id: string; name: string }[];
	selected: string[];
	disabled: boolean;
	onChange: (values: string[]) => void;
}) {
	const [open, setOpen] = useState(false);
	const [query, setQuery] = useState('');
	const root = useRef<HTMLDivElement>(null);
	const trigger = useRef<HTMLButtonElement>(null);
	const search = useRef<HTMLInputElement>(null);
	const id = useId();
	// Preserve and expose unavailable assignments instead of silently dropping them on save.
	const choices = [
		...options,
		...selected
			.filter((value) => !options.some((option) => option.id === value))
			.map((value) => ({ id: value, name: 'Unavailable assignment' })),
	];
	const names = selected.map((value) => choices.find((option) => option.id === value)!.name);
	useEffect(() => {
		if (!open) return;
		search.current?.focus();
		const outside = (event: PointerEvent) => {
			if (event.target instanceof Node && !root.current?.contains(event.target))
				setOpen(false);
		};
		document.addEventListener('pointerdown', outside);
		return () => document.removeEventListener('pointerdown', outside);
	}, [open]);
	useEffect(() => {
		if (disabled) setOpen(false);
	}, [disabled]);
	return (
		<div
			className="knowledge-picker"
			ref={root}
			onBlur={(event) => {
				if (!event.currentTarget.contains(event.relatedTarget as Node | null))
					setOpen(false);
			}}
			onKeyDown={(event) => {
				if (event.key === 'Escape' && open) {
					event.preventDefault();
					setOpen(false);
					trigger.current?.focus();
				}
			}}
		>
			<span id={`${id}-label`}>{label}</span>
			<button
				className="knowledge-picker-trigger"
				type="button"
				ref={trigger}
				aria-labelledby={`${id}-label ${id}-value`}
				aria-expanded={open}
				aria-controls={`${id}-choices`}
				disabled={disabled}
				onClick={() => {
					setQuery('');
					setOpen(!open);
				}}
			>
				<span id={`${id}-value`}>{names.length ? names.join(', ') : 'None selected'}</span>
			</button>
			{open && (
				<div className="knowledge-picker-popover" id={`${id}-choices`}>
					<input
						ref={search}
						type="search"
						onKeyDown={(event) => {
							if (event.key === 'Enter') event.preventDefault();
						}}
						aria-label={`Search ${label.toLowerCase()}`}
						placeholder="Search…"
						value={query}
						onChange={(event) => setQuery(event.currentTarget.value)}
					/>
					<div
						className="knowledge-picker-options"
						role="group"
						aria-label={`Choose ${label.toLowerCase()}`}
					>
						{choices
							.filter((option) =>
								option.name.toLowerCase().includes(query.toLowerCase()),
							)
							.map((option) => (
								<label className="knowledge-assignment-choice" key={option.id}>
									<input
										type="checkbox"
										checked={selected.includes(option.id)}
										onChange={() =>
											onChange(
												selected.includes(option.id)
													? selected.filter(
															(value) => value !== option.id,
														)
													: [...selected, option.id],
											)
										}
									/>
									<span>{option.name}</span>
								</label>
							))}
						{!choices.some((option) =>
							option.name.toLowerCase().includes(query.toLowerCase()),
						) && (
							<p>
								No {label.toLowerCase()}{' '}
								{options.length ? 'match your search.' : 'available.'}
							</p>
						)}
					</div>
				</div>
			)}
		</div>
	);
}
